import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { Category, Currency, JournalEntry, LedgerAccount, PlanItemView, PlanKind } from "../../entities/ledger/types";
import { humanError } from "../../shared/api/client";
import { amountValue, money, parseAmount, payeeName } from "../../shared/lib/format";
import { cancelPlanItem, createPlanItem, restorePlanItem, assignedSubjectId, unassignTransaction, updatePlanItem, type PlanningPeriod } from "./api";
import { AssignDialog } from "./AssignDialog";
import { Layered } from "./Layers";
import { PagerControls } from "../../shared/ui/Pager";
import { RowMenu } from "../../shared/ui/RowMenu";
import { ChipGroup } from "../../shared/ui/ChipGroup";
import { DateField } from "../../shared/ui/DateField";
import { Modal } from "../../shared/ui/Modal";
import { Select } from "../../shared/ui/Select";
import { usePager } from "../../shared/ui/usePager";
import { EmptyState } from "../../shared/ui/EmptyState";

const PLAN_PAGE = 10;

// A plan item lives in a period and may have a due date inside it. Both are
// stated on the row: a plan without its dates is only half a plan.
function dayLabel(value: string): string {
  return new Intl.DateTimeFormat("en", { day: "numeric", month: "short", timeZone: "UTC" }).format(new Date(value));
}

function periodLabel(start: string, end: string): string {
  const from = new Date(start);
  // The period is half-open, so the last day it covers is the day before it ends.
  const to = new Date(new Date(end).getTime() - 24 * 60 * 60 * 1000);
  const sameMonth = from.getUTCMonth() === to.getUTCMonth() && from.getUTCFullYear() === to.getUTCFullYear();
  if (sameMonth) {
    return `${from.getUTCDate()}–${to.getUTCDate()} ${new Intl.DateTimeFormat("en", { month: "short", timeZone: "UTC" }).format(from)}`;
  }
  return `${dayLabel(start)} – ${dayLabel(to.toISOString())}`;
}

interface PlanItemsPanelProps {
  bookID: string;
  baseCurrency: Currency;
  period: PlanningPeriod;
  items: PlanItemView[];
  categories: Category[];
  transactions: JournalEntry[];
  accounts: LedgerAccount[];
  onNotify: (message: string) => void;
}

const CURRENCY_OPTIONS: Currency[] = ["GEL", "EUR", "USD", "UAH", "GBP", "PLN", "CHF", "TRY", "RUB", "JPY", "KWD", "VND"];

export function PlanItemsPanel({ bookID, baseCurrency, period, items, categories, transactions, accounts, onNotify }: PlanItemsPanelProps) {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["planning"] });
  const [adding, setAdding] = useState(false);
  const [createRefused, setCreateRefused] = useState("");
  const [form, setForm] = useState({ kind: "expense" as PlanKind, name: "", amount: "", currency: baseCurrency, categoryId: "", dueDate: "", note: "" });
  const [showCancelled, setShowCancelled] = useState(false);
  const create = useMutation({
    mutationFn: () => createPlanItem(bookID, { ...form, amount: parseAmount(form.amount) ?? "", periodStart: period.start, periodEnd: period.end }),
    onSuccess: async (result) => {
      setForm({ kind: "expense", name: "", amount: "", currency: baseCurrency, categoryId: "", dueDate: "", note: "" });
      setAdding(false);
      await refresh();
      onNotify(`Plan "${result.planItem.name}" added.`);
    },
  });
  const categoryName = new Map(categories.map((category) => [category.id, category.name]));
  // Every transaction that already has a planning owner, so none is offered
  // twice. A transaction has one owner at most.
  const owners = new Map<string, string>();
  for (const view of items) {
    for (const assignment of view.assignments ?? []) {
      owners.set(assignedSubjectId(assignment), view.item.name);
    }
  }
  const filtered = items.filter((view) => showCancelled || view.status !== "cancelled");
  const pager = usePager(filtered, PLAN_PAGE);
  const visible = pager.visible;

  return (
    <section className="panel planning-panel" aria-labelledby="plan-items-title">
      <div className="panel-heading">
        <div><p className="eyebrow">Plan vs actual</p><h2 id="plan-items-title">Plan items</h2></div>
        <div className="heading-actions">
          <button className="chip" type="button" aria-pressed={showCancelled} onClick={() => setShowCancelled((value) => !value)}>Show cancelled</button>
          <button className="primary-button compact" type="button" onClick={() => setAdding((value) => !value)}><span>{adding ? "Close" : "Add plan item"}</span><span aria-hidden="true">{adding ? "×" : "＋"}</span></button>
        </div>
      </div>
      <Modal
        open={adding} onClose={() => setAdding(false)} title="Add a plan item"
        subtitle="A plan is an intention for this month. What actually happened is linked to it afterwards."
        footer={<>
          <button className="secondary-button" type="button" onClick={() => setAdding(false)}>Cancel</button>
          <button className="primary-button compact" type="submit" form="plan-item-form" disabled={create.isPending}>
            <span>{create.isPending ? "Saving…" : "Add plan"}</span><span aria-hidden="true">→</span>
          </button>
        </>}
      >
        <form id="plan-item-form" className="stacked-form" aria-label="Add plan item"
          onSubmit={(event) => {
            event.preventDefault();
            if (!form.name.trim() || parseAmount(form.amount) === null) {
              setCreateRefused("A plan item needs a name and an amount.");
              return;
            }
            setCreateRefused("");
            create.mutate();
          }}>
          <div className="field">
            <span className="field-label">Kind</span>
            <ChipGroup label="Plan kind" value={form.kind}
              onChange={(value) => setForm({ ...form, kind: value as PlanKind })}
              options={[{ value: "expense", label: "Expense" }, { value: "income", label: "Income" }]} />
          </div>
          <div className="field-grid">
            <label>Name<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Rent" required autoFocus /></label>
            <label>Amount<span className="amount-input"><input value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} inputMode="decimal" placeholder="700.00" required /><Select label="Currency" value={form.currency} onChange={(value) => setForm({ ...form, currency: value as Currency })} options={CURRENCY_OPTIONS.map((value) => ({ value, label: value }))} /></span></label>
            <div className="field"><span className="field-label" id="plan-category">Category</span>
              <Select labelledBy="plan-category" value={form.categoryId} placeholder="— none —"
                onChange={(categoryId) => setForm({ ...form, categoryId })}
                options={[{ value: "", label: "— none —" }, ...categories.map((category) => ({ value: category.id, label: category.name }))]} /></div>
            <DateField label="Due date" value={form.dueDate} min={period.start.slice(0, 10)} onChange={(dueDate) => setForm({ ...form, dueDate })} />
          </div>
          <label>Note<input value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} placeholder="optional" /></label>
          {createRefused ? <p className="form-error" role="alert">{createRefused}</p> : null}
          {create.error ? <p className="form-error" role="alert">{humanError(create.error)}</p> : null}
        </form>
      </Modal>
      {filtered.length === 0 ? <EmptyState>No plan items for this month. Plans describe expected income and expenses; allocations link them to what actually happened.</EmptyState> : null}
      <ul className="plan-list">
        {visible.map((view) => (
          <PlanItemRow key={view.item.id} bookID={bookID} baseCurrency={baseCurrency} view={view} categoryName={categoryName.get(view.item.categoryId ?? "")} transactions={transactions} accounts={accounts} owners={owners} onNotify={onNotify} onChanged={refresh} />
        ))}
      </ul>
      <PagerControls pager={pager} noun="plan items" step={PLAN_PAGE} />
    </section>
  );
}

interface PlanItemRowProps {
  bookID: string;
  baseCurrency: Currency;
  view: PlanItemView;
  categoryName?: string;
  transactions: JournalEntry[];
  accounts: LedgerAccount[];
  owners: Map<string, string>;
  onNotify: (message: string) => void;
  onChanged: () => Promise<void>;
}

function PlanItemRow({ bookID, baseCurrency, view, categoryName, transactions, accounts, owners, onNotify, onChanged }: PlanItemRowProps) {
  const [allocating, setAllocating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState(amountValue(view.item.amount.minor, view.item.amount.currency));
  const [refused, setRefused] = useState("");
  const item = view.item;
  // 385 allocated against a 200 plan is 193%, not 100%: the figure keeps the
  // overshoot, the fill is clamped because a bar cannot exceed its track.
  const percent = item.amount.minor > 0 ? Math.round((view.allocated.minor / item.amount.minor) * 100) : 0;
  const fill = Math.min(100, Math.max(0, percent));
  // Beating an income plan is good news; beating an expense plan is not. The
  // same arithmetic therefore reads two different ways, and only the costly
  // one is coloured as a problem.
  const overshootIsGood = item.kind === "income";

  const toggleCancel = useMutation({
    mutationFn: () => (item.cancelledAt ? restorePlanItem(bookID, item.id) : cancelPlanItem(bookID, item.id)),
    onSuccess: async () => { await onChanged(); onNotify(item.cancelledAt ? `"${item.name}" restored.` : `"${item.name}" cancelled.`); },
  });
  const save = useMutation({
    mutationFn: () => updatePlanItem(bookID, item.id, { amount: { amount: parseAmount(amount) ?? "", currency: item.amount.currency } }),
    onSuccess: async () => { setEditing(false); await onChanged(); onNotify(`"${item.name}" updated.`); },
  });
  const unassign = useMutation({
    mutationFn: (transactionID: string) => unassignTransaction(bookID, transactionID),
    onSuccess: async () => { await onChanged(); onNotify("The transaction no longer belongs to this plan. Its journal entry is unchanged."); },
  });

  return (
    <li className={`plan-row ${view.status} ${view.over ? (overshootIsGood ? "above" : "over") : ""}`}>
      <div className="plan-row-head">
        <div>
          <strong>{item.name}</strong>
          <small>
            {item.kind}
            {categoryName ? ` · ${categoryName}` : ""}
            {` · ${periodLabel(item.periodStart, item.periodEnd)}`}
            {item.dueDate ? ` · due ${dayLabel(item.dueDate)}` : ""}
          </small>
        </div>
        <span className="plan-row-status">
          <span className={`status-pill ${view.over ? (overshootIsGood ? "status-fulfilled" : "status-overdue") : `status-${view.status}`}`}>
            {view.over ? `${overshootIsGood ? "Above plan" : "Over plan"} · ${percent}%` : view.status.replace("_", " ")}
          </span>
          <RowMenu label={`Actions for ${item.name}`}>
            {item.cancelledAt ? null : <button className="row-menu-item" type="button" onClick={() => setAllocating((value) => !value)}>{allocating ? "Close allocation form" : "Allocate a transaction…"}</button>}
            {item.cancelledAt ? null : <button className="row-menu-item" type="button" onClick={() => setEditing((value) => !value)}>{editing ? "Close amount form" : "Change the planned amount…"}</button>}
            <button className="row-menu-item danger" type="button" disabled={toggleCancel.isPending} onClick={() => toggleCancel.mutate()}>{item.cancelledAt ? "Restore this plan" : "Cancel this plan"}</button>
          </RowMenu>
        </span>
      </div>
      <div className="plan-row-amounts">
        <div><span>Planned</span><Layered native={item.amount} base={view.plannedBase} reporting={view.plannedReporting} baseCurrency={baseCurrency} /></div>
        <div><span>Allocated</span><Layered native={view.allocated} base={view.allocatedBase} reporting={view.allocatedReporting} baseCurrency={baseCurrency} /></div>
        {view.over
          ? <div>
            <span>{overshootIsGood ? "Above plan by" : "Over by"}</span>
            <strong className={overshootIsGood ? "above-amount" : "over-amount"}>{money(view.overrun.minor, view.overrun.currency)}</strong>
            <small>{money(view.allocated.minor, view.allocated.currency)} against {money(item.amount.minor, item.amount.currency)} planned</small>
          </div>
          : <div><span>Remaining</span><strong>{money(view.remaining.minor, view.remaining.currency)}</strong></div>}
      </div>
      <div className="progress-track dark" role="progressbar" aria-label={`${item.name} fulfilled`}
        aria-valuemin={0} aria-valuemax={100} aria-valuenow={fill} aria-valuetext={`${percent}% of the plan`}>
        <span className={view.over && !overshootIsGood ? "over" : ""} style={{ width: `${fill}%` }} />
      </div>
      {(view.assignments ?? []).length > 0 ? (
        <ul className="allocation-list">
          {(view.assignments ?? []).map((assignment) => (
            <li key={assignedSubjectId(assignment)}>
              <span>{payeeName(assignment.description || "Transaction")}<small>{new Date(assignment.effectiveAt).toLocaleDateString("en", { day: "numeric", month: "short" })}</small></span>
              <strong>{assignment.amount.currency ? money(assignment.amount.minor, assignment.amount.currency) : money(assignment.bookValue.minor, assignment.bookValue.currency)}</strong>
              <button className="chip danger" type="button" disabled={unassign.isPending}
                onClick={() => unassign.mutate(assignedSubjectId(assignment))} title="This transaction stops belonging to this plan; the journal is untouched">Unassign</button>
            </li>
          ))}
        </ul>
      ) : null}

      <Modal
        open={editing} onClose={() => setEditing(false)} title="Change the planned amount"
        subtitle={item.name}
        footer={<>
          <button className="secondary-button" type="button" onClick={() => setEditing(false)}>Cancel</button>
          <button className="primary-button compact" type="submit" form={`plan-amount-${item.id}`} disabled={save.isPending}>
            <span>{save.isPending ? "Saving…" : "Save"}</span><span aria-hidden="true">→</span>
          </button>
        </>}
      >
        <form id={`plan-amount-${item.id}`} className="stacked-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (parseAmount(amount) === null) {
              setRefused("That is not an amount. Digits, and at most three decimals.");
              return;
            }
            setRefused("");
            save.mutate();
          }}>
          <label>Planned amount<span className="amount-input"><input value={amount} onChange={(event) => setAmount(event.target.value)} inputMode="decimal" autoFocus required /><span>{item.amount.currency}</span></span></label>
          <p className="panel-note">Only the intention changes. Allocations already linked to this plan stay as they are.</p>
          {refused ? <p className="form-error" role="alert">{refused}</p> : null}
          {save.error ? <p className="form-error" role="alert">{humanError(save.error)}</p> : null}
        </form>
      </Modal>
      <AssignDialog
        open={allocating} onClose={() => setAllocating(false)} bookID={bookID} view={view}
        transactions={transactions} accounts={accounts} owners={owners}
        onNotify={onNotify} onChanged={onChanged}
      />

      {toggleCancel.error || save.error || unassign.error ? <p className="form-error" role="alert">{humanError(toggleCancel.error ?? save.error ?? unassign.error)}</p> : null}
    </li>
  );
}
